import { DataType } from '../models/transaction-metadata.model'
import {
  DerivationState,
  EditTransactionCollection,
  EditTransactionField,
  EditTransactionMode,
  EditTransactionViewModel,
  lineKey,
} from './edit-transaction.projection'
import { asIsoDate, asNumber } from './format-field-value'

/**
 * The save-side half of the dialog (PVAL-26440): pure functions that turn what the dialog holds
 * into the record `POST helios-flex-transactions/transactions/save` takes.
 *
 * Nothing here formats for display. The dialog's display strings (`1,234.50`, `07/17/2026`) are a
 * rendering of a value, not the value, and posting them — or comparing them to decide what changed —
 * is the bug this layer exists to avoid. Every value is typed by its field's own `dataType`.
 */

/**
 * A value the dialog holds that cannot be sent as its field's type — `12.5` in an integer field,
 * `tomorrow` in a date. Raised rather than sent, so ENFN never sees a value the client knew was
 * wrong; the dialog names the field in its message.
 */
export class SaveAssemblyError extends Error {
  constructor(readonly field: string, readonly label: string) {
    super(`${label} does not hold a valid value`)
    this.name = 'SaveAssemblyError'
    // `instanceof` survives a down-levelled `extends Error` only with the prototype restored.
    Object.setPrototypeOf(this, SaveAssemblyError.prototype)
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Write [value] at a dotted [path] inside [target], creating plain objects on the way — the inverse
 * of the projection's `readPath`.
 *
 * `nearLeg.forwardDate` is a path into ONE nested object (an FX Swap has one near leg), so this never
 * creates an array: an intermediate that is missing, or is not a plain object, becomes `{}`. A second
 * child written under the same parent joins the first rather than replacing it.
 *
 * Mutates and returns [target], so a record can be built field by field.
 */
export function writePath(
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const keys = path.split('.')
  const last = keys.pop() as string
  let cursor = target
  for (const key of keys) {
    const next = cursor[key]
    if (!isPlainObject(next)) {
      const created: Record<string, unknown> = {}
      cursor[key] = created
      cursor = created
    } else {
      cursor = next
    }
  }
  cursor[last] = value
  return target
}

/** What {@link toWireValue} needs of a field: its name for the error, and the type it is sent as. */
export interface WireShape {
  field: string
  label: string
  dataType: DataType
}

const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim() === '')

/**
 * A field's value, typed for the wire by its `dataType`.
 *
 *  - `integer` — a number with no fraction (`'1,200'` → `1200`; `'12.5'` is an error);
 *  - `number`  — a number, accounting parentheses read as a minus (`'(1,234.50)'` → `-1234.5`);
 *  - `boolean` — `true` / `false`, from either the boolean or its text; anything else is an error;
 *  - `date`    — `yyyy-MM-dd`, from either the ISO or the US form;
 *  - `string`  — the text, unchanged.
 *
 * A list field (`enum` / `lookup`) needs no case of its own: the dialog stores the chosen option's
 * VALUE as the field's raw value, so it is typed like any other — an integer lookup's `'40554'` is
 * sent as `40554`.
 *
 * A blank answers `null`, and the caller decides whether that means "omit" (create) or "cleared"
 * (edit). An `array` / `object` field answers `undefined`: it is a container, never a value of its
 * own — its lines or its children are sent instead.
 *
 * @throws SaveAssemblyError when a non-blank value does not read as its type
 */
export function toWireValue(shape: WireShape, raw: unknown): unknown {
  if (shape.dataType === 'array' || shape.dataType === 'object') {
    return undefined
  }
  if (isBlank(raw)) return null
  const fail = (): never => {
    throw new SaveAssemblyError(shape.field, shape.label)
  }
  switch (shape.dataType) {
    case 'integer': {
      const numeric = asNumber(raw)
      return numeric !== null && Number.isInteger(numeric) ? numeric : fail()
    }
    case 'number': {
      const numeric = asNumber(raw)
      return numeric !== null ? numeric : fail()
    }
    case 'boolean': {
      if (raw === true || raw === false) return raw
      const text = String(raw).trim().toLowerCase()
      if (text === 'true') return true
      if (text === 'false') return false
      return fail()
    }
    case 'date': {
      const iso = asIsoDate(raw)
      return iso || fail()
    }
    default:
      return typeof raw === 'string' ? raw : String(raw)
  }
}

// ── Records ──────────────────────────────────────────────────────────────────────────────────────

/**
 * What the assembly reads off a dialog model. The single dialog's view model satisfies it as is; the
 * bulk grid builds one per row from that row's projected fields.
 */
export type SaveSource = Pick<
  EditTransactionViewModel,
  'mode' | 'sections' | 'footer' | 'collections'
> &
  Partial<Pick<EditTransactionViewModel, 'recordId'>>

/**
 * Read-only fields that are sent anyway: values the record needs that the user does not type — a GL
 * entry's ledger, a trade's custodian account — and that a derivation usually fills.
 *
 * Sent when valued. While their derivation is `pending` or `failed` they are omitted rather than
 * sent stale or blank, and never block Save: nobody can fill a field they cannot edit.
 */
export const SENT_READ_ONLY: ReadonlySet<string> = new Set([
  'generalLedgerId',
  'custodianAccountId',
])

/**
 * Called on every assembled collection line before it is sent — identity by default. The plug point
 * for line-level rules this ticket does not own (PVAL-26792: omit AutoCalc Other Payments).
 */
export type LinePlugin = (
  line: Record<string, unknown>,
  collection: EditTransactionCollection,
) => Record<string, unknown>

const identityLine: LinePlugin = line => line

/** One value the record can carry, after split halves are put back together. */
interface SaveEntry {
  key: string
  shape: WireShape
  raw: unknown
  editable: boolean
  derivationState: DerivationState
}

const SPLIT_HALF = /:(debit|credit)$/

const collectionKeyOfCollection = (
  collection: EditTransactionCollection,
): string => collection.key ?? collection.name

const isObjectContainer = (collection: EditTransactionCollection): boolean =>
  collection.dataType === 'object'

/**
 * Every value the dialog could send, as one list.
 *
 * Only `editable` fields and {@link SENT_READ_ONLY} are candidates. A collection's summary row is not
 * a value — its lines are sent instead — and nor is any `array` / `object` field. The two halves of a
 * top-level signed split are one signed number (`debit − credit`) under the field's own name: the
 * `:debit` / `:credit` ids are the dialog's, never the API's. A split over a collection's column is an
 * AGGREGATE of the lines, which are sent themselves, so it is skipped.
 */
function saveEntries(source: SaveSource): SaveEntry[] {
  const summaries = new Set(source.collections.map(entry => entry.name))
  const collectionKeys = new Set(
    source.collections.map(collectionKeyOfCollection),
  )
  const entries: SaveEntry[] = []
  const halves = new Map<string, EditTransactionField[]>()

  const fields = [
    ...source.sections.flatMap(section => section.fields),
    ...source.footer,
  ]
  for (const field of fields) {
    if (summaries.has(field.field)) continue
    if (field.dataType === 'array' || field.dataType === 'object') continue
    if (!field.editable && !SENT_READ_ONLY.has(field.field)) continue
    if (field.id !== field.field && SPLIT_HALF.test(field.id)) {
      halves.set(field.field, [...(halves.get(field.field) ?? []), field])
      continue
    }
    entries.push({
      key: field.field,
      shape: {
        field: field.field,
        label: field.label,
        dataType: field.dataType,
      },
      raw: field.rawValue,
      editable: field.editable,
      derivationState: field.derivationState,
    })
  }

  halves.forEach((pair, name) => {
    if (name.includes('.') && collectionKeys.has(name.split('.')[0])) return
    const amount = (side: 'debit' | 'credit'): number | null => {
      const half = pair.find(entry => entry.id.endsWith(`:${side}`))
      return isBlank(half?.rawValue) ? null : asNumber(half?.rawValue)
    }
    const debit = amount('debit')
    const credit = amount('credit')
    const [first] = pair
    entries.push({
      key: name,
      shape: { field: name, label: first.label, dataType: first.dataType },
      raw:
        debit === null && credit === null ? null : (debit ?? 0) - (credit ?? 0),
      editable: first.editable,
      derivationState: first.derivationState,
    })
  })
  return entries
}

/** A read-only value whose derivation has not answered is left out rather than sent stale. */
const withheld = (entry: SaveEntry): boolean =>
  !entry.editable &&
  SENT_READ_ONLY.has(entry.key) &&
  entry.derivationState !== 'idle'

/**
 * A collection's lines, as the arrays of objects the record carries — keyed by LEAF (`amount`, not
 * `otherPayments.amount`), each cell typed by its column's `dataType`.
 *
 *  - `edit` — a line that survives from the record starts from its RAW original object, so keys no
 *    column shows (a payment's `Links`) go back intact, and only its editable cells are written
 *    over it; a cell cleared from a value is sent as `null`. A removed line is simply absent.
 *  - a new line — and every line of a create or post — carries only its non-blank cells.
 *
 * @throws SaveAssemblyError when a cell does not read as its column's type
 */
export function assembleLines(
  collection: EditTransactionCollection,
  mode: EditTransactionMode,
  plugin: LinePlugin = identityLine,
): Record<string, unknown>[] {
  const originals = collection.originalLines ?? []
  return collection.rows.map((cells, index) => {
    const origin =
      mode !== 'edit'
        ? null
        : collection.lineOrigins
        ? collection.lineOrigins[index] ?? null
        : index < originals.length
        ? index
        : null
    const base =
      origin !== null && originals[origin] ? { ...originals[origin] } : null
    const line: Record<string, unknown> = base ?? {}
    collection.columns.forEach((column, slot) => {
      if (base && !column.editable) return
      const key = lineKey(column.field)
      const wire = toWireValue(
        { field: column.field, label: column.label, dataType: column.dataType },
        cells[slot],
      )
      if (wire === undefined) return
      if (wire === null) {
        if (base && !isBlank(base[key])) line[key] = null
        return
      }
      line[key] = wire
    })
    return plugin(line, collection)
  })
}

/** Children of an `object` container (an FX Swap leg), written by path — never an array. */
function writeObjectChildren(
  record: Record<string, unknown>,
  collection: EditTransactionCollection,
): void {
  const values = collection.objectValues ?? {}
  for (const column of collection.columns) {
    if (!column.editable) continue
    const wire = toWireValue(
      { field: column.field, label: column.label, dataType: column.dataType },
      values[column.field],
    )
    if (wire != null) writePath(record, column.field, wire)
  }
}

/**
 * The record a CREATE (or a post, which creates) sends: every candidate value that is not blank,
 * typed and nested, plus each editable collection's lines. Never an `id` — a post row's own
 * `tradeId` / `gLEntryId` belongs to the record it came from, not to the one being made.
 *
 * @throws SaveAssemblyError naming the first field whose value does not read as its type
 */
export function assembleCreateRecord(
  source: SaveSource,
  plugin: LinePlugin = identityLine,
): Record<string, unknown> {
  const record: Record<string, unknown> = {}
  for (const entry of saveEntries(source)) {
    if (withheld(entry)) continue
    const wire = toWireValue(entry.shape, entry.raw)
    if (wire == null) continue
    writePath(record, entry.key, wire)
  }
  for (const collection of source.collections) {
    if (collection.editable === false) continue
    if (isObjectContainer(collection)) {
      writeObjectChildren(record, collection)
      continue
    }
    const lines = assembleLines(collection, source.mode, plugin)
    if (lines.length) record[collectionKeyOfCollection(collection)] = lines
  }
  return record
}

/** What an edit is diffed against: each value's typed opening form, as a comparable key. */
export interface SaveBaseline {
  fields: ReadonlyMap<string, string>
  collections: ReadonlyMap<string, string>
}

/**
 * A value's typed form as a comparable string — so `07/17/2026` and `2026-07-17`, or `1,100` and
 * `1100`, are the same value. A value that does not type is keyed by its raw text: it compares
 * equal only to itself, and the error is raised when (and only if) it is actually sent.
 */
const wireKey = (entry: SaveEntry): string => {
  try {
    return JSON.stringify(toWireValue(entry.shape, entry.raw) ?? null)
  } catch {
    return `raw:${String(entry.raw)}`
  }
}

const linesKey = (collection: EditTransactionCollection): string => {
  try {
    return JSON.stringify(assembleLines(collection, 'edit'))
  } catch {
    return `raw:${JSON.stringify([collection.rows, collection.lineOrigins])}`
  }
}

/**
 * Snapshot the values an edit opened with. Taken when the dialog model is set — before any derived
 * answer is re-applied, so a derivation that changes the record's value counts as a change.
 */
export function editBaseline(source: SaveSource): SaveBaseline {
  return {
    fields: new Map(
      saveEntries(source).map(entry => [entry.key, wireKey(entry)]),
    ),
    collections: new Map(
      source.collections
        .filter(collection => !isObjectContainer(collection))
        .map(collection => [collection.name, linesKey(collection)]),
    ),
  }
}

/**
 * The record an EDIT sends: the record's `id` and ONLY what changed since {@link editBaseline}.
 *
 * A value is compared typed, never as display text. One cleared from a value is sent as `null`. A
 * collection is sent — whole — only when a line was changed, added or removed; an FX Swap leg is
 * never sent, since nothing in the dialog can change it.
 *
 * @throws SaveAssemblyError naming the first CHANGED field whose value does not read as its type
 */
export function assembleEditRecord(
  source: SaveSource,
  baseline: SaveBaseline,
  plugin: LinePlugin = identityLine,
): { record: Record<string, unknown>; hasChanges: boolean } {
  const record: Record<string, unknown> = { id: source.recordId ?? null }
  let changes = 0
  for (const entry of saveEntries(source)) {
    if (withheld(entry)) continue
    const before = baseline.fields.get(entry.key) ?? JSON.stringify(null)
    if (wireKey(entry) === before) continue
    writePath(record, entry.key, toWireValue(entry.shape, entry.raw) ?? null)
    changes++
  }
  for (const collection of source.collections) {
    if (collection.editable === false || isObjectContainer(collection)) {
      continue
    }
    const before = baseline.collections.get(collection.name)
    if (before !== undefined && linesKey(collection) === before) continue
    if (before === undefined && !collection.rows.length) continue
    record[collectionKeyOfCollection(collection)] = assembleLines(
      collection,
      'edit',
      plugin,
    )
    changes++
  }
  return { record, hasChanges: changes > 0 }
}
